import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRing, CheckCheck, Clock, ExternalLink, VolumeX, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useRing } from '../../lib/ring';
import { useAcknowledge, useSnooze } from '../../hooks/useAttention';
import { useSettings } from '../../hooks/useMe';
import { Button } from '../ui/Button';
import { spring } from '../ui/motion';
import { relativeTime } from '../../lib/format';

/** Full-width pulsing alert shown during a ring session. Closing it never acknowledges. */
export function RingAlert() {
  const ring = useRing();
  const ack = useAcknowledge();
  const snooze = useSnooze();
  const settings = useSettings();
  const navigate = useNavigate();
  const items = ring.alertItems;
  const open = items.length > 0;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[90] flex items-start justify-center p-3 sm:p-6">
          <motion.div className="absolute inset-0 bg-[#0b1220]/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="ring-title"
            aria-describedby="ring-desc"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={spring}
            className="relative w-full max-w-3xl overflow-hidden rounded-3xl border-2 border-danger bg-surface shadow-pop"
          >
            <div className="flex items-center gap-4 bg-danger px-5 py-4 text-white">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 ${ring.ringingNow ? 'ring-pulse' : ''}`}>
                <BellRing className="h-6 w-6" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <h2 id="ring-title" className="text-lg font-semibold">
                  {items.length === 1 ? 'An automatic response needs your attention' : `${items.length} items need your attention`}
                </h2>
                <p id="ring-desc" className="text-[0.8125rem] text-white/90">
                  This keeps ringing every {settings?.ring.intervalMinutes ?? 60} minutes until you press “Seen / Acknowledged”.
                </p>
              </div>
              {ring.ringingNow && (
                <Button size="sm" variant="ghost" className="!text-white hover:!bg-white/15" icon={<VolumeX className="h-4 w-4" />} onClick={ring.stopSound}>
                  Stop sound
                </Button>
              )}
              <button type="button" aria-label="Close alert (does not acknowledge)" title="Close (does not acknowledge)" onClick={ring.dismissAlert} className="rounded-xl p-2 hover:bg-white/15">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="max-h-[50vh] divide-y divide-line overflow-y-auto scroll-thin">
              {items.map((it) => (
                <li key={it.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-ink">{it.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-[0.8125rem] text-muted">{it.body}</p>
                    <p className="mt-1 text-xs text-muted">
                      Created {relativeTime(it.createdAt)} · rang {it.ringCount}×
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {it.threadId && (
                      <Button
                        size="md"
                        icon={<ExternalLink className="h-4 w-4" />}
                        onClick={() => {
                          ring.dismissAlert();
                          navigate(`/inbox/${it.threadId}`);
                        }}
                      >
                        Open email
                      </Button>
                    )}
                    <Button size="md" icon={<Clock className="h-4 w-4" />} loading={snooze.isPending && snooze.variables?.id === it.id} onClick={() => snooze.mutate({ id: it.id })}>
                      Snooze {settings?.ring.snoozeMinutes ?? 30}m
                    </Button>
                    <Button size="md" variant="primary" icon={<CheckCheck className="h-4 w-4" />} loading={ack.isPending && ack.variables === it.id} onClick={() => ack.mutate(it.id)}>
                      Seen / Acknowledged
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <p className="border-t border-line bg-raised px-5 py-2.5 text-xs text-muted">
              Opening the email or closing this alert does not acknowledge it. Only “Seen / Acknowledged” stops future rings — on every device.
            </p>
          </motion.section>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
